import { useEffect, useMemo, useRef, useState } from "react";
import { groupBlocks, usedByOthers } from "@/lib/blocks";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import FairCustomerPicker from "@/components/fair/FairCustomerPicker";
import FairModelBlock from "@/components/fair/FairModelBlock";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { tl, orderTotals } from "@/lib/fair";
import FairTotals from "@/components/fair/FairTotals";

const uid = () => Math.random().toString(36).slice(2);
const row = (block = uid(), model = "") => ({ id: uid(), block, model, fair_product_id: "", koli: "", pairs: "8", price: "" });

export default function FairOrderDialog({ open, onOpenChange, order, products, customers, onCustomersChanged, onSaved }) {
  const [customerId, setCustomerId] = useState("");
  const [note, setNote] = useState("");
  const [discount, setDiscount] = useState("0");
  const [vat, setVat] = useState("10");
  const [rows, setRows] = useState([row()]);
  const [saving, setSaving] = useState(false);
  const byId = (id) => products.find((p) => p.id === id);
  const productsRef = useRef(products);
  productsRef.current = products;

  useEffect(() => {
    if (!open) return;
    setCustomerId(order?.customer_id || ""); setNote(order?.note || "");
    setDiscount(String(order?.discount_pct ?? 0)); setVat(String(order?.vat_pct ?? 10));
    setRows(order?.items?.length ? order.items.map((it) => {
      const m = productsRef.current.find((p) => p.id === it.fair_product_id)?.model_code || "";
      return { ...row(`m-${m}`, m), fair_product_id: it.fair_product_id, koli: String(it.koli),
        pairs: String(it.pairs || 8), price: it.price ? String(it.price) : "" };
    }) : [row()]);
  }, [open, order]);

  const blocks = useMemo(() => groupBlocks(rows), [rows]);
  const usedModels = useMemo(() => usedByOthers(blocks), [blocks]);
  const setRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const setModel = (block, model) => setRows((prev) => {
    const at = prev.findIndex((r) => r.block === block);
    const rest = prev.filter((r) => r.block !== block);
    const fresh = row(block, model);
    const colors = products.filter((p) => p.model_code === model);
    if (colors.length === 1) fresh.fair_product_id = colors[0].id;
    rest.splice(at, 0, fresh); return rest;
  });
  const addColor = (block, model) => setRows((prev) => {
    const last = prev.map((r) => r.block).lastIndexOf(block);
    const next = [...prev]; next.splice(last + 1, 0, row(block, model)); return next;
  });
  // New card from the dialog: put it on the given row (model block) or a new row in that block
  const placeCard = async (card, rowId, isModel) => {
    await onCustomersChanged();
    setRows((prev) => {
      if (isModel) {
        const block = prev.find((r) => r.id === rowId).block;
        const at = prev.findIndex((r) => r.block === block);
        const rest = prev.filter((r) => r.block !== block);
        rest.splice(at, 0, { ...row(block, card.model_code), fair_product_id: card.id });
        return rest;
      }
      if (rowId) return prev.map((r) => (r.id === rowId ? { ...r, fair_product_id: card.id } : r));
      const block = prev.find((r) => r.model === card.model_code)?.block;
      const last = prev.map((r) => r.block).lastIndexOf(block);
      const next = [...prev]; next.splice(last + 1, 0, { ...row(block, card.model_code), fair_product_id: card.id }); return next;
    });
  };
  const total = rows.reduce((a, r) => a + (parseInt(r.koli) || 0), 0);

  const submit = async () => {
    if (!customerId) { toast.error("Müşteri seçin"); return; }
    for (const r of rows) {
      if (!r.model) { toast.error("Her model için model kodu seçin"); return; }
      if (!r.fair_product_id) { toast.error(`${r.model}: renk seçin`); return; }
      if (!(parseInt(r.koli) > 0)) { toast.error(`${r.model} ${byId(r.fair_product_id)?.model_color}: koli adedi girin`); return; }
      if (!(parseInt(r.pairs) > 0)) { toast.error(`${r.model}: koli içi çift sayısı girin`); return; }
    }
    setSaving(true);
    try {
      const body = { customer_id: customerId, note, discount_pct: parseFloat(discount) || 0, vat_pct: parseFloat(vat) || 0, items: rows.map((r) => ({ fair_product_id: r.fair_product_id, koli: parseInt(r.koli),
        pairs: parseInt(r.pairs), price: parseFloat(String(r.price).replace(",", ".")) || 0 })) };
      if (order) await api.put(`/fair/orders/${order.id}`, body);
      else await api.post("/fair/orders", body);
      toast.success(order ? "Fuar siparişi güncellendi" : "Fuar siparişi kaydedildi");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="fair-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">{order ? "Fuar Siparişini Düzenle" : "Yeni Fuar Siparişi"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-[#6B5B4A] mb-1 block">Müşteri *</Label>
            <FairCustomerPicker value={customerId} onChange={setCustomerId} customers={customers} onCreated={onCustomersChanged} />
          </div>
          <div>
            <Label className="text-xs text-[#6B5B4A]">Not</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="fair-order-note" className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
          </div>
          {products.length === 0 && <p className="text-sm text-[#B45309]" data-testid="fair-order-no-products">Önce &ldquo;Fuar Kartları&rdquo; sekmesinden model ve renk ekleyin.</p>}
          {blocks.map((bl, idx) => (
            <FairModelBlock key={bl[0].block} idx={idx} rows={bl} products={products}
              usedModels={usedModels[idx]}
              onModel={(m) => setModel(bl[0].block, m)} onRow={setRow} onAddColor={() => addColor(bl[0].block, bl[0].model)}
              onRemoveRow={(id) => setRows((prev) => prev.filter((r) => r.id !== id))}
              onCreated={placeCard}
              onRemoveBlock={blocks.length > 1 ? () => setRows((prev) => prev.filter((r) => r.block !== bl[0].block)) : null} />
          ))}
          <Button variant="outline" onClick={() => setRows((prev) => [...prev, row()])} data-testid="fair-add-model"
            className="border-[#DCCFB8] bg-[#EFE6D6] text-[#B45309] hover:bg-[#FFFBF4]"><Plus className="w-4 h-4 mr-2" /> Model Ekle</Button>
          <FairTotals rows={rows} discount={discount} vat={vat} onDiscount={setDiscount} onVat={setVat} />
        </div>
        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div>
              <p className="text-[10px] font-mono uppercase text-[#6B5B4A] tracking-widest">Toplam</p>
              <p className="font-mono text-xl font-bold text-[#B45309]" data-testid="fair-order-total">{total} koli · {tl(orderTotals({ items: rows, discount_pct: discount, vat_pct: vat }).grand)}</p>
            </div>
            <Button onClick={submit} disabled={saving} data-testid="fair-order-submit"
              className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-6">
              {saving ? "Kaydediliyor..." : order ? "Değişiklikleri Kaydet" : "Fuar Siparişini Kaydet"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
